import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { InfoHint } from "@/components/InfoHint";
import { cn } from "@/lib/utils";
import { Landmark, RefreshCw, Loader2 } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, YAxis, Tooltip } from "recharts";
import { usd, pct, pnlCls } from "./polymarket/format";
import { getPortfolioEquity, snapshotPortfolioEquity } from "@/lib/api";
import { toast } from "sonner";

const Metric = ({ label, value, cls, sub, testid }) => (
  <div className="space-y-0.5">
    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className={cn("text-sm font-mono tabular-nums", cls)} data-testid={testid}>{value}</p>
    {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
  </div>
);

export const CapitalCard = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { getPortfolioEquity(180).then(setD).catch(() => {}); }, []);
  useEffect(() => { load(); const t = setInterval(load, 300000); return () => clearInterval(t); }, [load]);

  const snap = async () => {
    setBusy(true);
    try { await snapshotPortfolioEquity(); toast.success("Snapshot equity registrato"); load(); }
    catch (e) { toast.error("Snapshot fallito", { description: String(e?.message || e) }); }
    finally { setBusy(false); }
  };

  if (!d) return null;
  const m = d.metrics || {};
  const bl = d.baseline || {};
  const per = bl.perVenue || {};
  const curve = (m.index || []).map((p) => ({ day: p.day, v: p.index }));
  const short = m.nDays < 10;

  return (
    <Card className="border-border/70 bg-card/60" data-testid="capital-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex flex-wrap items-center gap-2">
          <Landmark className="h-4 w-4 text-[hsl(var(--primary))]" />
          Capitale versato & rendimento vero (TWR)
          <InfoHint testId="info-capital" text="Equity reale contro capitale VERSATO. Il TWR (time-weighted return) neutralizza versamenti e prelievi: un bonifico non è un profitto. Sharpe e drawdown sono calcolati sull'indice TWR giornaliero, non sul P&L realizzato." />
          <Badge variant="outline" data-testid="capital-days-badge"
            className={cn("gap-1.5 font-mono", short ? "border-amber-500/50 text-amber-300" : "border-emerald-500/40 text-emerald-300")}>
            {m.nDays ?? 0} giorni di serie
          </Badge>
          <Button size="sm" variant="outline" className="ml-auto" data-testid="capital-snapshot-btn"
            onClick={snap} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
            Registra ora
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Metric label="Capitale versato" value={usd(bl.totalUsd)} testid="capital-baseline"
            sub={`HL ${usd(per.hyperliquid)} · CT ${usd(per.ctrader)} · PM ${usd(per.polymarket)}`} />
          <Metric label="Equity oggi" value={usd(d.equityUsd)} testid="capital-equity"
            sub={d.today?.readyVenues?.length ? `${d.today.readyVenues.join(", ")}` : "nessuna venue leggibile"} />
          <Metric label="P&L contabile" value={usd(d.pnlUsd)} cls={pnlCls(d.pnlUsd)}
            sub={d.venuesMissing?.length
              ? `${pct(d.pnlPct)} · solo su ${usd(d.baselineReadyUsd)} versati (mute: ${d.venuesMissing.join(", ")})`
              : pct(d.pnlPct)} testid="capital-pnl" />
          <Metric label="TWR cumulato" value={pct(m.twrPct)} cls={pnlCls(m.twrPct)} testid="capital-twr"
            sub={m.dailyVolPct != null ? `vol g. ${m.dailyVolPct}%` : ""} />
          <Metric label="Sharpe (ann. 365)" testid="capital-sharpe"
            value={m.sharpe == null ? "—" : m.sharpe}
            cls={m.enoughForSharpe ? pnlCls(m.sharpe) : "text-amber-300"}
            sub={m.enoughForSharpe ? `Sortino ${m.sortino ?? "—"}` : "campione insufficiente (<30g)"} />
          <Metric label="Max drawdown" value={m.maxDrawdownPct == null ? "—" : `-${m.maxDrawdownPct}%`}
            cls="text-rose-300" testid="capital-maxdd"
            sub={m.worstDayPct != null ? `giorno peggiore ${m.worstDayPct}%` : ""} />
        </div>
        {curve.length >= 2 && (
          <div className="h-24" data-testid="capital-curve">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={curve}>
                <YAxis domain={["dataMin", "dataMax"]} hide />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }}
                  formatter={(v) => [`indice ${v}`, ""]} labelFormatter={(l) => curve[l]?.day || ""} />
                <Line type="monotone" dataKey="v" dot={false} strokeWidth={2}
                  stroke={(m.twrPct ?? 0) >= 0 ? "hsl(142 70% 45%)" : "hsl(0 70% 55%)"} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
        <p className="text-[11px] leading-relaxed text-muted-foreground" data-testid="capital-note">
          {d.note}
          {bl.fx?.rate ? ` Cambio EURUSD ${bl.fx.rate} (età ${bl.fx.ageDays}g).` : " Cambio EURUSD non disponibile: il totale in USD potrebbe essere incompleto."}
          {m.skippedDays?.length ? ` Giorni esclusi dal calcolo: ${m.skippedDays.map((s) => `${s.day} (${s.why})`).join(", ")}.` : ""}
        </p>
      </CardContent>
    </Card>
  );
};
